import { useState, useEffect, useCallback } from 'react';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import Modal, { ModalProps } from '../../../../components/template/mvc-modal';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import Input from '../../../../components/atoms/input';
import MovementService, {
	IMovement,
} from '../../../../services/Movement.service';
import InputCalendar from '../../../../components/molecules/input-calendar';

interface TravelModalProps extends ModalProps<IMovement> {
	setModalProps: React.Dispatch<React.SetStateAction<ModalProps<IMovement>>>;
}

const TravelModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	isModalVisible,
	...props
}: TravelModalProps) => {
	const { t } = useTranslation();

	const [error, setError] = useState<string>('');
	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		onEdit();
	};

	const validate = (): boolean => {
		if (!data.date || !data.travelCode) {
			setError('alert.mandatory');
			return false;
		}
		return true;
	};

	const onEdit = async () => {
		const response = await MovementService.edit(data, setError);
		if (!response) return;
		NotificationToast.Success(t('alert.success'));
		onClose(true);
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};

	const handleDate = (date: Date) => {
		handleInput({ target: { name: 'date', value: date } });
	};

	const renderInputs = () => (
		<div className="grid gap-x-2 gap-y-3">
			<Input
				value={data.travelCode}
				name="travelCode"
				label={t('movement.travel_code')}
				placeholder={t('movement.travel_code')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<InputCalendar
				date={data.date}
				name="date"
				label={t('date.title')}
				placeholder={t('date.title')}
				onDateChange={handleDate}
				disabled={mode === 'view'}
				required
			/>
		</div>
	);

	return (
		<Modal<IMovement>
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			isModalVisible={isModalVisible}
			containerClassName="overflow-visible"
			{...props}
		>
			{renderInputs()}
		</Modal>
	);
};

export default TravelModal;
